'use client';

import Error from '@/components/core/error';
import styles from './page.module.scss';
import { getMember } from '@/services/members.service';
import IrlMemberContribution from '@/components/page/member-details/member-irl-contributions';
import { ProfileDetails } from '@/components/page/member-details/ProfileDetails';
import { ContactDetails } from '@/components/page/member-details/ContactDetails';
import { ExperienceDetails } from '@/components/page/member-details/ExperienceDetails';
import { ContributionsDetails } from '@/components/page/member-details/ContributionsDetails';
import { RepositoriesDetails } from '@/components/page/member-details/RepositoriesDetails';
import { OneClickVerification } from '@/components/page/member-details/OneClickVerification';
import { TeamsDetails } from '@/components/page/member-details/TeamsDetails';
import { OfficeHoursDetails } from '@/components/page/member-details/OfficeHoursDetails';
import { InvestorProfileDetails } from '@/components/page/member-details/InvestorProfileDetails';
import { BackButton } from '@/components/ui/BackButton';
import React, { useEffect, use } from 'react';
import { BookWithOther } from '@/components/page/member-details/BookWithOther';
import { getMemberListForQuery } from '@/app/actions/members.actions';
import qs from 'qs';
import clsx from 'clsx';
import { isDemodaySignUpSource, isMemberAvailableToConnect } from '@/utils/member.utils';
import { useCurrentUserStore } from '@/services/auth/store';
import { getCookiesFromClient } from '@/utils/third-party.helper';
import { useQuery } from '@tanstack/react-query';
import { IMember } from '@/types/members.types';
import { useSearchParams, useRouter } from 'next/navigation';
import { AccountCreatedView } from '@/components/page/member-details/AccountCreatedView';

import MemberPageLoader from './loading';
import Head from 'next/head';
import { MembersQueryKeys } from '@/services/members/constants';
import { useGetMemberInvestorSettings } from '@/services/members/hooks/useGetMemberInvestorSettings';
import { ForumActivity } from '@/components/page/member-details/ForumActivity';
import { isAdminUser } from '@/utils/user/isAdminUser';

const shouldShowInvestorProfileForThirdParty = (
  member: IMember,
  isOwner: boolean,
  isAdmin: boolean,
  isInvestor?: boolean,
): boolean => {
  if (!isOwner && !isAdmin) {
    return false;
  }

  if (isInvestor === null || isInvestor) {
    return true;
  }

  return false;
};

const MemberDetails = (props: { params: Promise<any> }) => {
  const params = use(props.params);
  const memberId = params?.id;
  const searchParams = useSearchParams();
  const router = useRouter();

  const { currentUser: userInfo } = useCurrentUserStore();
  const isAdmin = isAdminUser(userInfo);
  const isOwner = !!userInfo && userInfo.uid === memberId;
  const isLoggedIn = !!userInfo;

  // Check for prefillEmail and returnTo parameters
  const prefillEmail = searchParams.get('prefillEmail');
  const returnTo = searchParams.get('returnTo');
  const shouldShowAccountCreated = !isLoggedIn && prefillEmail && returnTo;
  const {
    data: member,
    isError,
    isLoading,
  } = useQuery({
    queryKey: [MembersQueryKeys.GET_MEMBER, memberId, isLoggedIn, userInfo?.uid],
    queryFn: () =>
      getMember(
        memberId,
        { with: 'image,skills,location,teamMemberRoles.team' },
        isLoggedIn,
        userInfo,
        !isAdmin && !isOwner,
        true,
      ),
    enabled: !!memberId,
    select: (data) => data?.data?.formattedData,
  });

  // Fetch investor settings to check visibility preference
  const { data: memberInvestorSettings } = useGetMemberInvestorSettings(memberId);
  const { authToken } = getCookiesFromClient();
  const { data: availableToConnectCount } = useQuery({
    queryKey: ['memberList'],
    queryFn: () => getMemberListForQuery(qs.stringify({ hasOfficeHours: true }), 1, 1, authToken),
    enabled: !!authToken,
    select: (data) => data?.total,
  });
  const { currentUser } = useCurrentUserStore();
  const isAvailableToConnect = isMemberAvailableToConnect(member);
  const showOtherConnectOptions =
    !isAvailableToConnect && isLoggedIn && currentUser?.rbac?.status === 'APPROVED' && !isOwner;
  const status = member?.rbac?.status;
  const isNewInvestor = status === 'PENDING' && isOwner && isDemodaySignUpSource(member?.signUpSource);

  // Scroll to top when member data is loaded or member ID changes
  useEffect(() => {
    if (member && !isLoading) {
      document.body.scrollTo({
        top: 0,
        left: 0,
        behavior: 'instant',
      });
    }
  }, [member, memberId, isLoading]);

  // Handle login click from AccountCreatedView
  const handleLoginClick = () => {
    // Stay on the same page and add #login hash
    router.push(`${window.location.pathname}${window.location.search}#login`);
  };

  // Show AccountCreatedView if user is not logged in and has prefillEmail and returnTo params
  if (shouldShowAccountCreated) {
    return <AccountCreatedView onLoginClick={handleLoginClick} />;
  }

  if (isError) {
    return <Error />;
  }

  if (isLoading) {
    return <MemberPageLoader />;
  }

  if (!member) {
    return <Error title="This member doesn't exist or isn't approved yet" description="Member not found" />;
  }

  function renderPageContent() {
    if (!member) {
      return null;
    }

    const showInvestorProfile = shouldShowInvestorProfileForThirdParty(
      member,
      isOwner,
      isAdmin,
      memberInvestorSettings?.isInvestor,
    );
    const isInvestorOnly =
      isNewInvestor || member.rbac.policies?.every((p: { role: string }) => p.role.toLowerCase() === 'investor');

    return (
      <>
        <OneClickVerification
          userInfo={userInfo}
          member={member}
          isLoggedIn={isLoggedIn}
          isNewInvestor={isNewInvestor}
        />
        <ProfileDetails userInfo={userInfo} member={member} isLoggedIn={isLoggedIn} />
        {showInvestorProfile && (
          <InvestorProfileDetails
            userInfo={userInfo}
            member={member}
            isLoggedIn={isLoggedIn}
            isInvestor={memberInvestorSettings?.isInvestor}
            useInlineAddTeam
          />
        )}
        <OfficeHoursDetails userInfo={userInfo} member={member} isLoggedIn={isLoggedIn} />
        <ContactDetails userInfo={userInfo} member={member} isLoggedIn={isLoggedIn} />
        <ForumActivity member={member} userInfo={userInfo} isOwner={isOwner} />
        <TeamsDetails member={member} isLoggedIn={isLoggedIn} userInfo={userInfo} />
        {!isInvestorOnly && (
          <>
            <ExperienceDetails userInfo={userInfo} member={member} isLoggedIn={isLoggedIn} />
            <ContributionsDetails userInfo={userInfo} member={member} isLoggedIn={isLoggedIn} />
          </>
        )}

        {member.eventGuests.length > 0 && (
          <div className={styles?.memberDetail__irlContribution}>
            <IrlMemberContribution member={member} userInfo={userInfo} />
          </div>
        )}
        {!isInvestorOnly && <RepositoriesDetails userInfo={userInfo} member={member} isLoggedIn={isLoggedIn} />}
      </>
    );
  }

  return (
    <>
      <Head>
        <title>{`${member?.name} | Protocol Labs Directory`}</title>
      </Head>
      <div className={styles?.memberDetail}>
        <div
          className={clsx(styles.container, {
            [styles.singleColumn]: isAvailableToConnect || !isLoggedIn || isOwner || !showOtherConnectOptions,
          })}
        >
          <div className={styles.content}>
            <BackButton to={`/members`} />
            <div
              className={clsx(styles?.memberDetail__container, {
                [styles.centered]: isAvailableToConnect || isOwner,
              })}
            >
              {renderPageContent()}
            </div>
          </div>
          {showOtherConnectOptions && (
            <div className={styles.desktopOnly}>
              <div style={{ visibility: 'hidden' }}>
                <BackButton to={`/members`} />
              </div>
              <BookWithOther count={availableToConnectCount} member={member} />
            </div>
          )}
        </div>

        {/* {userInfo.uid === member.id && (
          <>
            <SubscribeToRecommendationsWidget userInfo={userInfo} />
            <UpcomingEventsWidget userInfo={userInfo} />
          </>
        )} */}
      </div>
    </>
  );
};

export default MemberDetails;
